import { useState, useEffect, useCallback } from "react";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { toast } from "sonner";
import { Loader2, CheckCircle2, XCircle, Clock, Phone, RefreshCw, Filter, AlertTriangle, MessageSquare, Copy, Check } from "lucide-react";

const STATUS_META = {
  pending: { label: "En attente", color: "#F59E0B", bg: "#FEF3C7", icon: Clock },
  approved: { label: "Approuvée", color: "#10B981", bg: "#D1FAE5", icon: CheckCircle2 },
  rejected: { label: "Rejetée", color: "#EF4444", bg: "#FEE2E2", icon: XCircle },
};

const CHECK_FIELDS = [
  { key: "sms", label: "SMS de confirmation reçu" },
  { key: "amount", label: "Montant correct" },
  { key: "reference", label: "Référence correspond à mon relevé" },
  { key: "phone", label: "Numéro du chauffeur correspond" },
];

function formatDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch { return iso; }
}

function ageMinutes(iso) {
  if (!iso) return 0;
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));
}

function formatAge(mins) {
  if (mins < 1) return "à l'instant";
  if (mins < 60) return `il y a ${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h < 24) return m > 0 ? `il y a ${h}h ${m}min` : `il y a ${h}h`;
  const d = Math.floor(h / 24);
  return `il y a ${d}j`;
}

function ageBadge(mins) {
  if (mins < 30) return { label: formatAge(mins), color: "#10B981", bg: "#D1FAE5" };
  if (mins < 60) return { label: formatAge(mins), color: "#F59E0B", bg: "#FEF3C7" };
  return { label: formatAge(mins), color: "#EF4444", bg: "#FEE2E2" };
}

export default function TFTPaymentRequestsPanel() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [actionLoading, setActionLoading] = useState(null);
  const [rejectDialog, setRejectDialog] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [checks, setChecks] = useState({}); // {reqId: {sms:true,...}}
  const [copiedRef, setCopiedRef] = useState(null);
  const [, setTick] = useState(0); // forces re-render of ages every 30s

  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 30000);
    return () => clearInterval(id);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = filter === "all" ? "/admin/tft/payment-requests" : `/admin/tft/payment-requests?status=${filter}`;
      const { data } = await api.get(url);
      setRequests(data || []);
    } catch (e) {
      toast.error("Erreur chargement", { description: e.response?.data?.detail || "Erreur" });
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const handleApprove = async (req) => {
    const reqChecks = checks[req.id] || {};
    const allChecked = CHECK_FIELDS.every((f) => reqChecks[f.key]);
    if (!allChecked) {
      const ok = window.confirm("Tous les points de vérification ne sont pas cochés. Approuver quand même ?");
      if (!ok) return;
    }
    setActionLoading(req.id);
    try {
      await api.post(`/admin/tft/payment-requests/${req.id}/approve`);
      toast.success("Demande approuvée", { description: `+${req.total_credit} TFT crédité à ${req.driver_name}` });
      load();
    } catch (e) {
      toast.error("Échec de l'approbation", { description: e.response?.data?.detail || "Erreur" });
    } finally {
      setActionLoading(null);
    }
  };

  const toggleCheck = (reqId, field) => {
    setChecks((prev) => ({
      ...prev,
      [reqId]: { ...(prev[reqId] || {}), [field]: !(prev[reqId]?.[field]) },
    }));
  };

  const copyRef = async (ref, reqId) => {
    try {
      await navigator.clipboard.writeText(ref);
      setCopiedRef(reqId);
      setTimeout(() => setCopiedRef(null), 1500);
    } catch { /* noop */ }
  };

  const submitReject = async () => {
    if (!rejectDialog) return;
    if (!rejectReason.trim()) {
      toast.error("Veuillez préciser la raison du rejet");
      return;
    }
    setActionLoading(rejectDialog.id);
    try {
      await api.post(`/admin/tft/payment-requests/${rejectDialog.id}/reject`, { reason: rejectReason.trim() });
      toast.success("Demande rejetée");
      setRejectDialog(null);
      setRejectReason("");
      load();
    } catch (e) {
      toast.error("Échec du rejet", { description: e.response?.data?.detail || "Erreur" });
    } finally {
      setActionLoading(null);
    }
  };

  const counts = requests.reduce((acc, r) => { acc[r.status] = (acc[r.status] || 0) + 1; return acc; }, {});

  return (
    <div className="space-y-4" data-testid="tft-payment-requests-panel">
      <div className="bg-gradient-to-br from-[#0A2540] to-[#1E3A5F] rounded-2xl p-5 text-white">
        <div className="flex items-center gap-2 mb-1">
          <Phone className="w-5 h-5 text-[#FACC15]" />
          <h2 className="text-lg font-bold" style={{ fontFamily: 'Outfit' }}>Validation des paiements TFT</h2>
        </div>
        <p className="text-sm text-white/70">Vérifiez les références de transaction Mobile Money soumises par les chauffeurs et créditez leurs comptes TFT.</p>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <Filter className="w-4 h-4 text-[#64748B]" />
        {["pending", "approved", "rejected", "all"].map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`px-3 py-1.5 text-xs font-semibold rounded-full transition-colors ${filter === s ? "bg-[#0A2540] text-white" : "bg-white text-[#64748B] border border-[#CBD5E1] hover:border-[#0A2540]"}`}
            data-testid={`tft-payment-filter-${s}`}
          >
            {s === "all" ? "Toutes" : STATUS_META[s]?.label || s}
            {s !== "all" && counts[s] !== undefined && <span className="ml-1 opacity-70">({counts[s]})</span>}
          </button>
        ))}
        <button onClick={load} className="ml-auto h-7 w-7 rounded-full flex items-center justify-center hover:bg-[#F8FAFC] border border-[#CBD5E1]" title="Rafraîchir" data-testid="tft-payment-refresh">
          <RefreshCw className={`w-3.5 h-3.5 text-[#64748B] ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {/* List */}
      <div className="bg-white rounded-2xl border border-[#CBD5E1] overflow-hidden">
        {loading ? (
          <div className="py-12 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#0A2540]" /></div>
        ) : requests.length === 0 ? (
          <div className="py-12 text-center text-[#64748B]">
            <Phone className="w-8 h-8 mx-auto mb-2 opacity-40" />
            <p className="text-sm">Aucune demande {filter !== "all" ? STATUS_META[filter]?.label?.toLowerCase() : ""}</p>
          </div>
        ) : (
          <ul className="divide-y divide-[#E2E8F0]">
            {requests.map((r) => {
              const meta = STATUS_META[r.status] || STATUS_META.pending;
              const Icon = meta.icon;
              const isProcessing = actionLoading === r.id;
              const age = ageMinutes(r.created_at);
              const ageBdg = ageBadge(age);
              const isStale = r.status === "pending" && age >= 60;
              const reqChecks = checks[r.id] || {};
              const checkedCount = CHECK_FIELDS.filter((f) => reqChecks[f.key]).length;
              return (
                <li key={r.id} className={`p-4 hover:bg-[#F8FAFC] ${isStale ? "bg-red-50/30" : ""}`} data-testid={`tft-payment-request-${r.id}`}>
                  <div className="flex items-start gap-3 mb-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <p className="text-sm font-bold text-[#0A2540]">{r.driver_name}</p>
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ color: meta.color, backgroundColor: meta.bg }}>
                          <Icon className="w-3 h-3" /> {meta.label}
                        </span>
                        {r.status === "pending" && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ color: ageBdg.color, backgroundColor: ageBdg.bg }} title={formatDate(r.created_at)}>
                            <Clock className="w-3 h-3" /> {ageBdg.label}
                          </span>
                        )}
                        {isStale && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold text-[#EF4444] bg-[#FEE2E2]" title="Délai de traitement dépassé">
                            <AlertTriangle className="w-3 h-3" /> Urgent
                          </span>
                        )}
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1 text-xs text-[#64748B] mt-2">
                        <div className="flex items-center gap-1.5">
                          <span>📞</span>
                          {r.driver_phone ? (
                            <a href={`tel:${r.driver_phone}`} className="text-[#3B82F6] hover:underline font-medium" data-testid={`call-driver-${r.id}`} title="Appeler le chauffeur">
                              {r.driver_phone}
                            </a>
                          ) : <span>—</span>}
                          {r.driver_phone && (
                            <a href={`https://wa.me/${r.driver_phone.replace(/[^\d]/g, "")}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-semibold text-[#25D366] hover:bg-[#25D366]/10" title="Contacter sur WhatsApp" data-testid={`whatsapp-driver-${r.id}`}>
                              <MessageSquare className="w-2.5 h-2.5" /> WA
                            </a>
                          )}
                        </div>
                        <p>🏦 {r.operator_label}</p>
                        <p>💰 {(r.amount || 0).toLocaleString()} XAF</p>
                        <p>🎁 +{(r.bonus || 0).toLocaleString()} bonus</p>
                        <div className="col-span-1 sm:col-span-2 flex items-center gap-1.5">
                          <span>📋 Référence:</span>
                          <button onClick={() => copyRef(r.reference, r.id)} className="font-mono font-semibold text-[#0A2540] select-all bg-[#F8FAFC] px-1.5 py-0.5 rounded hover:bg-[#FACC15]/20 flex items-center gap-1" data-testid={`copy-ref-${r.id}`} title="Copier la référence">
                            {r.reference}
                            {copiedRef === r.id ? <Check className="w-3 h-3 text-[#10B981]" /> : <Copy className="w-3 h-3 text-[#64748B]" />}
                          </button>
                        </div>
                        {r.phone_number && <p className="col-span-1 sm:col-span-2">📱 N° payé: <span className="text-[#0A2540]">{r.phone_number}</span></p>}
                        <p className="col-span-1 sm:col-span-2">🕐 Soumise: {formatDate(r.created_at)}</p>
                      </div>
                      {r.status === "rejected" && r.rejection_reason && (
                        <p className="text-xs text-[#EF4444] mt-2 bg-[#FEE2E2] rounded p-2"><strong>Raison du rejet:</strong> {r.rejection_reason}</p>
                      )}
                      {r.status === "approved" && r.processed_by_name && (
                        <p className="text-[10px] text-[#10B981] mt-1">Approuvée par {r.processed_by_name} le {formatDate(r.processed_at)}</p>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-lg font-bold text-[#0A2540]">{(r.total_credit || 0).toLocaleString()}</p>
                      <p className="text-[10px] text-[#64748B] uppercase">TFT à créditer</p>
                    </div>
                  </div>

                  {r.status === "pending" && (
                    <>
                      <div className="mt-3 p-3 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg" data-testid={`checklist-${r.id}`}>
                        <p className="text-[11px] font-bold text-[#0A2540] mb-2 flex items-center gap-1.5 uppercase tracking-wide">
                          <CheckCircle2 className="w-3.5 h-3.5 text-[#10B981]" /> Checklist de vérification
                          <span className="ml-auto text-[10px] font-normal text-[#64748B]">{checkedCount}/{CHECK_FIELDS.length}</span>
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                          {CHECK_FIELDS.map((f) => (
                            <label key={f.key} className="flex items-center gap-2 cursor-pointer text-xs text-[#0A2540] hover:text-[#0A2540]" data-testid={`check-${f.key}-${r.id}-label`}>
                              <input
                                type="checkbox"
                                checked={!!reqChecks[f.key]}
                                onChange={() => toggleCheck(r.id, f.key)}
                                className="w-3.5 h-3.5 accent-[#10B981]"
                                data-testid={`check-${f.key}-${r.id}`}
                              />
                              <span className={reqChecks[f.key] ? "line-through text-[#94A3B8]" : ""}>{f.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      <div className="flex gap-2 pt-3 mt-2 border-t border-[#E2E8F0]">
                        <Button
                          onClick={() => handleApprove(r)}
                          disabled={isProcessing}
                          size="sm"
                          className={`flex-1 text-white h-9 ${checkedCount === CHECK_FIELDS.length ? "bg-[#10B981] hover:bg-[#059669]" : "bg-[#10B981]/70 hover:bg-[#10B981]"}`}
                          data-testid={`approve-tft-request-${r.id}`}
                          title={checkedCount === CHECK_FIELDS.length ? "Tous les contrôles validés" : "Approuver (un avertissement s'affichera car tous les contrôles ne sont pas validés)"}
                        >
                          {isProcessing ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1" />} Approuver
                        </Button>
                        <Button
                          onClick={() => { setRejectDialog(r); setRejectReason(""); }}
                          disabled={isProcessing}
                          size="sm"
                          variant="outline"
                          className="flex-1 border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444]/10 h-9"
                          data-testid={`reject-tft-request-${r.id}`}
                        >
                          <XCircle className="w-3.5 h-3.5 mr-1" /> Rejeter
                        </Button>
                      </div>
                    </>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Reject dialog */}
      <Dialog open={!!rejectDialog} onOpenChange={(o) => !o && setRejectDialog(null)}>
        <DialogContent className="sm:max-w-sm" data-testid="tft-payment-reject-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#EF4444]" style={{ fontFamily: 'Outfit' }}>Rejeter la demande</DialogTitle>
            <DialogDescription className="text-xs text-[#64748B]">
              Indiquez la raison du rejet. Elle sera communiquée au chauffeur.
            </DialogDescription>
          </DialogHeader>
          {rejectDialog && (
            <div className="space-y-3">
              <div className="bg-[#F8FAFC] rounded-lg p-3 text-xs space-y-1">
                <p><strong>Chauffeur:</strong> {rejectDialog.driver_name}</p>
                <p><strong>Référence:</strong> <span className="font-mono">{rejectDialog.reference}</span></p>
                <p><strong>Montant:</strong> {(rejectDialog.amount || 0).toLocaleString()} XAF</p>
              </div>
              <Input
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Ex: Référence introuvable côté MTN"
                className="h-10 border-[#CBD5E1]"
                data-testid="tft-payment-reject-reason"
                autoFocus
              />
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setRejectDialog(null)} className="flex-1 border-[#CBD5E1]" data-testid="cancel-reject-tft">Annuler</Button>
                <Button onClick={submitReject} disabled={actionLoading === rejectDialog.id} className="flex-1 bg-[#EF4444] text-white hover:bg-[#DC2626]" data-testid="confirm-reject-tft">
                  {actionLoading === rejectDialog.id ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <XCircle className="w-3.5 h-3.5 mr-1" />} Confirmer le rejet
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
